import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Add,
  Edit,
  MoreVert,
  Person,
  Email as EmailIcon,
  Business,
  Shield,
  CheckCircle,
  Cancel,
  Visibility,
  Block,
} from '@mui/icons-material';
import { useCountyUsersList, useSuspendCountyUser, useUnsuspendCountyUser } from '../../hooks';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../theme/theme';

export const CountyUsersPage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const { users, isLoading, error } = useCountyUsersList(page + 1);
  const suspendUser = useSuspendCountyUser();
  const unsuspendUser = useUnsuspendCountyUser();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedUser(null);
  };

  const handleViewProfile = () => {
    if (selectedUser) {
      navigate(`/superadmin/county-users/${selectedUser.id}`);
      handleMenuClose();
    }
  };

  const handleEdit = () => {
    if (selectedUser) {
      navigate(`/superadmin/county-users/${selectedUser.id}/edit`);
      handleMenuClose();
    }
  };

  const handleSuspend = async () => {
    if (!selectedUser) return;

    try {
      await suspendUser.mutateAsync({ countyId: selectedUser.county_id, userId: selectedUser.id });
      showSnackbar('User suspended successfully', 'success');
      handleMenuClose();
    } catch (error) {
      showSnackbar('Failed to suspend user', 'error');
    }
  };

  const handleUnsuspend = async () => {
    if (!selectedUser) return;

    try {
      await unsuspendUser.mutateAsync({ countyId: selectedUser.county_id, userId: selectedUser.id });
      showSnackbar('User activated successfully', 'success');
      handleMenuClose();
    } catch (error) {
      showSnackbar('Failed to activate user', 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person sx={{ fontSize: 18, color: colors.grey[600] }} />
          <Box>
            <Box sx={{ fontWeight: 600 }}>{params.value}</Box>
            <Box sx={{ fontSize: '0.75rem', color: colors.grey[600] }}>{params.row.email}</Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'county',
      headerName: 'County',
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.county?.name || 'N/A',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business sx={{ fontSize: 16, color: colors.grey[500] }} />
          <span>{params.value}</span>
        </Box>
      ),
    },
    {
      field: 'user_type',
      headerName: 'User Type',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          color="primary"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'roles',
      headerName: 'Roles',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {params.value && params.value.length > 0 ? (
            params.value.map((role: any) => (
              <Chip
                key={role.id}
                icon={<Shield sx={{ fontSize: 14 }} />}
                label={role.name}
                size="small"
                color="secondary"
                sx={{ fontSize: '0.75rem' }}
              />
            ))
          ) : (
            <Chip label="No roles" size="small" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const isActive = params.value === 1;
        return (
          <Chip
            icon={isActive ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
            label={isActive ? 'Active' : 'Suspended'}
            color={isActive ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueGetter: (value) => (value ? new Date(value).toLocaleDateString() : 'N/A'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="County Users"
          subtitle="Manage users across all counties"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Counties' },
            { label: 'County Users' },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load county users. Please try again.
          </Alert>
        )}

        {/* Summary Cards */}
        {!isLoading && users && (
          <Grid container spacing={2.5} mb={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.primary.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.primary.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Total County Users</Box>
                    <Person sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {users.meta?.total?.toLocaleString() || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Across all counties</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.success.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.success.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Active Users</Box>
                    <CheckCircle sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {users.data?.filter((u: any) => u.status !== 'inactive').length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Currently active</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.secondary.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.secondary.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>With Roles</Box>
                    <Shield sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {users.data?.filter((u: any) => u.roles && u.roles.length > 0).length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Users with assigned roles</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.info.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.info.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Showing</Box>
                    <Business sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>{users.data?.length || 0}</Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>On current page</Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            overflow: 'hidden',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={users?.data || []}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{ page, pageSize }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setPageSize(model.pageSize);
                  }}
                  rowCount={users?.meta?.total || 0}
                  paginationMode="server"
                  disableRowSelectionOnClick
                  autoHeight
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '16px 20px',
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[100]}`,
                      fontSize: '0.875rem',
                      py: 1.5,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.grey[700],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: `${colors.primary.main}08`,
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleViewProfile}>
            <ListItemIcon>
              <Visibility fontSize="small" sx={{ color: colors.primary.main }} />
            </ListItemIcon>
            <ListItemText>View Profile</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" sx={{ color: colors.warning.main }} />
            </ListItemIcon>
            <ListItemText>Edit User</ListItemText>
          </MenuItem>
          {selectedUser?.is_active === 1 ? (
            <MenuItem onClick={handleSuspend}>
              <ListItemIcon>
                <Block fontSize="small" sx={{ color: colors.error.main }} />
              </ListItemIcon>
              <ListItemText>Suspend User</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem onClick={handleUnsuspend}>
              <ListItemIcon>
                <CheckCircle fontSize="small" sx={{ color: colors.success.main }} />
              </ListItemIcon>
              <ListItemText>Activate User</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>
    </AdminLayout>
  );
};
